import React, { useState, useEffect, useMemo } from 'react';
import { Row, Col, Card, Empty, Spin, Alert, Typography, Space } from 'antd';
import { useTranslation } from 'react-i18next';
import { leaveService } from '../../services/leaveService';
import { formatCalendarInterval } from '../../utils/calendarUtils';
import MiniMonthGrid from './MiniMonthGrid';
import './DepartmentTimeOffOverview.css';

const { Text } = Typography;

const DECEMBER = 11;

// Parse a 'YYYY-MM-DD' string as UTC midnight, consistent with calendarUtils.js.
const parseUtcMidnight = (isoDate) => new Date(isoDate + 'T00:00:00Z');

const toIsoDate = (date) => date.toISOString().slice(0, 10);

const DepartmentTimeOffOverview = ({ departmentId }) => {
  const { t, i18n } = useTranslation();
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [fetchError, setFetchError] = useState(false);

  // Range: first day of the CURRENT month → December 31 of the CURRENT year.
  // The year is derived from the runtime date — never a hardcoded literal.
  const range = useMemo(() => {
    const now = new Date();
    const year = now.getUTCFullYear();
    const currentMonth = now.getUTCMonth();
    const start = new Date(Date.UTC(year, currentMonth, 1));
    const end = new Date(Date.UTC(year, DECEMBER, 31));
    return { year, currentMonth, start: toIsoDate(start), end: toIsoDate(end) };
  }, []);

  useEffect(() => {
    let cancelled = false;
    const fetchEvents = async () => {
      setLoading(true);
      setFetchError(false);
      try {
        const data = await leaveService.getCalendarEvents(range.start, range.end, departmentId);
        if (!cancelled) {
          setEvents(data.filter((event) => event.type === 'LEAVE'));
        }
      } catch (error) {
        console.error('Error fetching department time-off overview:', error);
        if (!cancelled) {
          setFetchError(true);
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    };
    fetchEvents();
    return () => {
      cancelled = true;
    };
  }, [departmentId, range.start, range.end]);

  // For each month from the current month through December, keep only those with
  // at least one overlapping leave event (holiday-only / empty months omitted).
  // Overlap is the inclusive test: event.start <= lastDayOfMonth && event.end >= firstDayOfMonth.
  // Note: a weekend-only leave (e.g. Sat→Sun) passes this filter but MiniMonthGrid renders
  // zero covered cells for it — accepted behavior, as such leaves book 0 working days.
  const monthsWithTimeOff = useMemo(() => {
    const result = [];
    for (let month = range.currentMonth; month <= DECEMBER; month += 1) {
      const firstOfMonth = new Date(Date.UTC(range.year, month, 1));
      const lastOfMonth = new Date(Date.UTC(range.year, month + 1, 0));
      const monthEvents = events.filter((event) => {
        const start = parseUtcMidnight(event.start);
        const end = parseUtcMidnight(event.end);
        return start <= lastOfMonth && end >= firstOfMonth;
      });
      if (monthEvents.length > 0) {
        result.push({ month, events: monthEvents });
      }
    }
    return result;
  }, [events, range.year, range.currentMonth]);

  // One readable row per leave request across the whole range, deduped by id and
  // sorted by start date then employee name — the textual counterpart to the
  // mini-grids (whose names are hover-only). Inclusive dates, localized type.
  const leaveList = useMemo(() => {
    const byId = new Map();
    events.forEach((event) => {
      if (!byId.has(event.id)) {
        byId.set(event.id, event);
      }
    });
    return Array.from(byId.values()).sort(
      (a, b) =>
        (a.start || '').localeCompare(b.start || '') ||
        (a.employeeName || '').localeCompare(b.employeeName || ''),
    );
  }, [events]);

  if (loading) {
    return (
      <div className="department-overview-loading" data-testid="overview-loading">
        <Spin size="large" />
      </div>
    );
  }

  if (fetchError) {
    return (
      <Alert
        type="error"
        showIcon
        message={t('calendar.fetchError')}
        data-testid="overview-error"
      />
    );
  }

  if (monthsWithTimeOff.length === 0) {
    return (
      <Empty
        description={t('calendar.noTimeOffInRange')}
        data-testid="overview-empty"
      />
    );
  }

  return (
    <>
      <Row gutter={[16, 16]} data-testid="department-timeoff-overview">
        {monthsWithTimeOff.map(({ month, events: monthEvents }) => (
          <Col xs={24} sm={12} lg={8} xl={6} key={month}>
            <Card size="small" bodyStyle={{ padding: '0.75rem' }}>
              <MiniMonthGrid year={range.year} month={month} events={monthEvents} />
            </Card>
          </Col>
        ))}
      </Row>

      {leaveList.length > 0 && (
        <div
          className="department-overview-leave-list"
          data-testid="overview-leave-list"
          style={{ marginTop: '1rem' }}
        >
          <Text strong>{t('calendar.employeesOnLeaveUpcoming')}</Text>
          <Space direction="vertical" size={2} style={{ width: '100%', marginTop: 4 }}>
            {leaveList.map((leave) => (
              <Text
                key={leave.id}
                data-testid={`overview-leave-row-${leave.id}`}
                style={{ fontSize: '0.8125rem' }}
              >
                {leave.employeeName}: {formatCalendarInterval(leave.start, leave.end, i18n.language)}
                {' '}
                <Text type="secondary" style={{ fontSize: '0.75rem' }}>
                  ({t(`leave.form.types.${leave.leaveType}`)}
                  {leave.status === 'PENDING' ? ` ${t('calendar.pending')}` : ''})
                </Text>
              </Text>
            ))}
          </Space>
        </div>
      )}
    </>
  );
};

export default DepartmentTimeOffOverview;
